/**
 * Microphone access + MediaRecorder format selection (implementation-plan.md §1.3b).
 *
 * The recorder container is only an intermediate: every segment is decoded, pre-processed and
 * re-encoded to 16 kHz mono WAV before upload. What matters here is (a) getting a stream with
 * the browser's own noise processing on, and (b) picking a container the current browser can
 * actually record — iOS Safari records `audio/mp4` (AAC-LC), never WebM/Opus.
 */

export type MicFailure =
  | 'unsupported'
  | 'insecure-context'
  | 'denied'
  | 'no-device'
  | 'in-use'
  | 'unknown';

export class MicError extends Error {
  constructor(
    readonly failure: MicFailure,
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'MicError';
  }
}

/** Plan §1.3b step 1 — let the browser do the first pass, never a custom noise model. */
export const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

/** Candidate containers, best first for desktop; iOS Safari only offers `audio/mp4`. */
export const CAPTURE_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/ogg;codecs=opus',
] as const;

export function isIosSafari(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  return iOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/** Null when this browser cannot record audio at all. */
export function pickCaptureMime(preferMp4 = isIosSafari()): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = preferMp4
    ? (['audio/mp4', ...CAPTURE_MIME_CANDIDATES] as const)
    : CAPTURE_MIME_CANDIDATES;
  return candidates.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null;
}

export function mediaDevicesSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export interface MicStream {
  stream: MediaStream;
  trackLabel: string;
  /** What the browser actually applied — iOS forces echo cancellation on regardless. */
  settings: MediaTrackSettings;
  mimeType: string | null;
}

export async function openMic(
  constraints: MediaTrackConstraints = MIC_CONSTRAINTS,
  nav: Navigator = navigator,
): Promise<MicStream> {
  if (!mediaDevicesSupported()) {
    throw new MicError('unsupported', 'This browser cannot capture audio (getUserMedia is unavailable)');
  }
  if (typeof nav === 'object' && 'isSecureContext' in globalThis && globalThis.isSecureContext === false) {
    throw new MicError(
      'insecure-context',
      'Microphone capture needs a secure context (https, or localhost during development)',
    );
  }

  let stream: MediaStream;
  try {
    stream = await nav.mediaDevices.getUserMedia({ audio: constraints, video: false });
  } catch (error) {
    const name = (error as { name?: string } | null)?.name ?? '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      throw new MicError('denied', 'Microphone permission was refused — allow it in your browser settings', error);
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      throw new MicError('no-device', 'No microphone was found on this device', error);
    }
    if (name === 'NotReadableError' || name === 'AbortError') {
      throw new MicError('in-use', 'The microphone is already in use by another app', error);
    }
    throw new MicError('unknown', `Could not open the microphone: ${String(error)}`, error);
  }

  const track = stream.getAudioTracks()[0];
  if (!track) {
    throw new MicError('no-device', 'The microphone stream has no audio track');
  }
  if (typeof MediaRecorder === 'undefined') {
    throw new MicError('unsupported', 'This browser cannot record audio (MediaRecorder is unavailable)');
  }

  return {
    stream,
    trackLabel: track.label || 'microphone',
    settings: track.getSettings?.() ?? {},
    mimeType: pickCaptureMime(),
  };
}

export function closeMic(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

/** Human-readable guidance for the UI (plan §1.3b step 3). */
export function micFailureAdvice(failure: MicFailure): string {
  switch (failure) {
    case 'denied':
      return 'Allow microphone access for this site, then start the session again.';
    case 'no-device':
      return 'Connect a microphone (or a headset) and try again.';
    case 'in-use':
      return 'Close the app that is using the microphone, then try again.';
    case 'unsupported':
      return 'Use Chrome, Edge or Safari to tag with your voice.';
    case 'insecure-context':
      return 'Open Callout over https (or localhost) to use the microphone.';
    default:
      return 'Try holding the phone closer, or restart the session.';
  }
}
